import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import dayjs from 'dayjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { RestRequestService } from '../restApi/rest-request.service';
import {
  FORECAST_MODIFICATIONS,
  PRIME_SPEND,
  REST_BUDGET_ADJUSTMENTS,
  REST_BUDGET_HISTORY,
  REST_CALC_DISTRIBUTION,
  REST_COMMITTED_ITEMS,
  REST_LINE_ITEMS_SUMMARY,
  REST_MANAGER_SPEND,
  REST_PROJECT_ITEMS,
  REST_PROJECTS,
} from '../restApi/RestRoutes';
import { ErrorHandlerRest } from '../restApi/errorHandler-rest';
import {
  ICommittedItem,
  ICommittedItemPayload,
  IDistributionResponse,
  ILineItem,
  ILineItemPayload,
  IPrimeSpendDistribution,
  ISpendDistribution,
  SpendDistributionRequest,
} from '../store/spend/spend.interfaces';
import { CurrentUserService } from './current-user.service';
import { StorageService } from './storage.service';
import { IProjectSpendGC, LineItemSummary, ProjectSpendMgResponse } from './project-spend.types';
import {
  BudgetAdjustment,
  BudgetHistoryData,
  BudgetHistoryLineItem,
} from '@app/pages/webapp/projects/view-project/spend/project-budget-history/project-budget-history.types';

@Injectable({
  providedIn: 'root',
})
export class ProjectSpendService extends ErrorHandlerRest {
  isLoading = new BehaviorSubject<boolean>(false);
  DATE_FORMAT = 'MM/DD/YYYY';

  // this is displayed in the spend overview component
  // left here to keep the functionality and interface
  get showZeroLines(): boolean {
    return this.storage.getShowZeroLineItems();
  }

  set showZeroLines(value: boolean) {
    this.storage.setShowZeroLineItems(value);
  }

  constructor(
    private rest: RestRequestService,
    protected user: CurrentUserService,
    protected router: Router,
    private storage: StorageService,
  ) {
    super(user, router);
  }

  getAll(projectId: number, showZeroLines?: boolean): Promise<ProjectSpendMgResponse> {
    if (!projectId) {
      return new Promise((res, rej) => rej({ message: 'Project ID is not defined' }));
    }

    if (showZeroLines === undefined) {
      showZeroLines = this.showZeroLines;
    }

    this.isLoading.next(true);
    return new Promise<ProjectSpendMgResponse>((res, rej) => {
      this.rest
        .get(
          `${REST_PROJECTS}/${projectId}/${REST_MANAGER_SPEND}`,
          {},
          { showZeroDollarLines: showZeroLines ? 1 : 0 },
        )
        .then(
          (data) => {
            this.isLoading.next(false);
            res(data);
          },
          (error) => {
            rej(this.handleError(error));
          },
        );
    });
  }

  getAllGc(projectId: number, showZeroLines?: boolean): Observable<IProjectSpendGC> {
    if (!projectId) {
      return throwError(() => new Error('Project ID is not defined'));
    }

    if (showZeroLines === undefined) {
      showZeroLines = this.showZeroLines;
    }

    this.isLoading.next(true);

    return this.rest
      .getWithObservable(
        `${REST_PROJECTS}/${projectId}/${PRIME_SPEND}`,
        {},
        { showZeroDollarLines: showZeroLines ? 1 : 0 },
      )
      .pipe(
        map((data: IProjectSpendGC) => {
          // todo: get row number from backend, remove this and make not optional
          data.items.forEach((item, i) => {
            item.row_number = i;
          });
          return data;
        }),
        catchError((error) => {
          return throwError(() => this.handleError(error));
        }),
        finalize(() => {
          this.isLoading.next(false);
        }),
      );
  }

  getAllWithObservable(
    projectId: number,
    showZeroLines?: boolean,
  ): Observable<ProjectSpendMgResponse> {
    if (!projectId) {
      return throwError(() => ({ message: 'Project ID is not defined' }));
    }
    if (showZeroLines === undefined) {
      showZeroLines = this.showZeroLines;
    }
    return this.rest.getWithObservable(
      `${REST_PROJECTS}/${projectId}/${REST_MANAGER_SPEND}`,
      {},
      { showZeroDollarLines: showZeroLines ? 1 : 0 },
    );
  }

  getSpendLineItemDetail(lineItemId: number) {
    return this.rest.getWithObservable(`${REST_PROJECT_ITEMS}/${lineItemId}/spend-detail`);
  }

  getLineItemsSummary(projectId: number, onlySubLines: boolean): Observable<LineItemSummary[]> {
    let params = {};
    if (onlySubLines) {
      params = {
        onlySublines: 1,
      };
    }

    return this.rest.getWithObservable(
      `${REST_PROJECTS}/${projectId}/${REST_LINE_ITEMS_SUMMARY}`,
      {},
      params,
    );
  }

  getBudgetAdjustments(projectId: number) {
    const params = {
      showZeroDollarLines: 0,
    };

    return this.rest
      .getWithObservable(`${REST_PROJECTS}/${projectId}/${REST_BUDGET_HISTORY}`, {}, params)
      .pipe(
        map((budgetLineItems: BudgetHistoryLineItem[]) => {
          const sortedAdjustments = this.sortAdjustments(
            budgetLineItems.flatMap((item) => item.budget_adjustments),
          );
          const dates: string[] = this.getAdjustmentDates(budgetLineItems);
          const filteredByLineItem = this.formatForTable(budgetLineItems);
          const totals = this.calculateTotals(budgetLineItems);

          return { lineItems: filteredByLineItem, dates, totals, sortedAdjustments };
        }),
      );
  }

  adjustBudget(formData) {
    return new Promise((resolve, reject) => {
      this.rest.post(REST_BUDGET_ADJUSTMENTS, formData).then(
        (data) => {
          resolve(data);
        },
        (err) => {
          reject(this.handleError(err));
        },
      );
    });
  }

  modifyForecast(body) {
    return new Promise((resolve, reject) => {
      this.rest.post(FORECAST_MODIFICATIONS, body).then(
        (data) => {
          resolve(data);
        },
        (err) => {
          reject(this.handleError(err));
        },
      );
    });
  }

  private sortAdjustments(adjustments: BudgetAdjustment[]): BudgetAdjustment[] {
    adjustments.sort((a, b) => {
      if (dayjs(a.updated_at).isBefore(dayjs(b.updated_at))) {
        return -1;
      }
      return 1;
    });
    return adjustments;
  }

  private getAdjustmentDates(lineItems: BudgetHistoryLineItem[]): string[] {
    const sortedAdjustments = this.sortAdjustments(
      lineItems.flatMap((item) => item.budget_adjustments),
    );

    const dates: string[] = sortedAdjustments.map((adjust) =>
      dayjs(adjust.updated_at).format(this.DATE_FORMAT),
    );

    return dates;
  }

  private formatForTable(lineItems: BudgetHistoryLineItem[]) {
    const formattedItems: BudgetHistoryData[] = lineItems.map((item) => ({
      id: item.id,
      name: item.name,
      original_budget: item.original_budget,
      current_budget: item.current_budget,
      adjustments: [],
    }));

    const sortedAdjustments = this.sortAdjustments(
      lineItems.flatMap((item) => item.budget_adjustments),
    );

    sortedAdjustments.forEach((adjust, adjustIndex) => {
      const fromItem = formattedItems.find((i) => i.id === adjust.from_item_id);
      const toItem = formattedItems.find((i) => i.id === adjust.to_item_id);

      // all values default to 0, this is overwritten below at some line items
      formattedItems.forEach((item) => {
        item.adjustments.push({
          date: dayjs(adjust.updated_at).format(this.DATE_FORMAT),
          is_external_adjustment: adjust.is_external_adjustment,
          value: 0,
        });
      });

      // if it's an external adjustment it doesn't have a source line item
      if (!adjust.is_external_adjustment) {
        fromItem.adjustments[adjustIndex].value = -adjust.value;
      }
      toItem.adjustments[adjustIndex].value = adjust.value;
    });

    return formattedItems;
  }

  calculateTotals(lineItems: BudgetHistoryLineItem[]) {
    const sortedAdjustments = this.sortAdjustments(
      lineItems.flatMap((item) => item.budget_adjustments),
    );

    const totals = {
      original_budget: 0,
      current_budget: 0,
      adjustments: [],
    };
    lineItems.forEach((item) => {
      totals.original_budget += item.original_budget;
      totals.current_budget += item.current_budget;
    });

    // this is kept for legacy code
    sortedAdjustments.forEach((record) => {
      totals.adjustments.push({
        date: dayjs(record.updated_at).format(this.DATE_FORMAT),
        value: record.is_external_adjustment ? record.value : 0,
      });
    });

    return totals;
  }

  getLineItemsByProjectId(id: number, includeBudgetAdjustments = null) {
    // const params: any = { fiscal_view: 1 };
    const params: any = { fiscal_view: 1 };
    if (includeBudgetAdjustments) {
      params.with_budget_adjustment = 1;
    }
    // return this.rest.getWithObservable(`${REST_LINE_ITEMS_BY_PROJECT}/${id}/default`, {}, params);
    return this.rest.getWithObservable(`${REST_PROJECTS}/${id}/items/default`, {}, params);
  }

  createLineItem$(lineItem: ILineItem) {
    const payload = this.getLineItemPayload(lineItem, false);

    return this.rest.postWithObservable(REST_PROJECT_ITEMS, payload);
  }

  // get only necessary data
  getLineItemPayload(lineItem: ILineItem, isUpdate: boolean): ILineItemPayload {
    console.log('get line item payload', lineItem, isUpdate);
    const payload: ILineItemPayload = {
      name: lineItem.name,
      budget: lineItem.budget,
      duration: lineItem.duration,
      start_date: dayjs(lineItem.start_date).format('YYYY-MM-DD'),
      distribution: lineItem.distribution,
      row_number: lineItem.row_number,
      project_id: lineItem.project_id,
    };
    if (isUpdate) {
      payload.id = lineItem.id;
    }
    if ('prime_start_date' in lineItem && this.user.isPremiumSP) {
      // this is a prime line item, set prime-specific fields
      payload.prime_start_date = dayjs(lineItem.prime_start_date).format('YYYY-MM-DD');
      payload.prime_duration = lineItem.prime_duration;
      payload.prime_distribution = lineItem.prime_distribution;
    }

    console.log('line item payload', payload);

    return payload;
  }

  updateLineItem(lineItem: ILineItem) {
    const payload = this.getLineItemPayload(lineItem, true);

    return this.rest
      .putWithObservable(`${REST_PROJECT_ITEMS}/${payload.id}?fiscal_view=1`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // get only necessary data
  getCommittedItemPayload(lineItem: ICommittedItem): ICommittedItemPayload {
    const payload = {
      id: lineItem.id,
      row_number: lineItem.row_number,
      committed_row_number: lineItem.committed_row_number,
      budget: lineItem.budget,
      distribution: lineItem.distribution,
      project_id: lineItem.project_id,
    };

    return payload;
  }

  updateCommittedLineItem(lineItem: ICommittedItem) {
    const payload = this.getCommittedItemPayload(lineItem);

    return this.rest
      .patchWithObservable(`${REST_COMMITTED_ITEMS}/${payload.id}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deleteLineItem(id: number) {
    return this.rest.delWithObservable(`${REST_PROJECT_ITEMS}/${id}`);
  }

  calculateDistribution<T extends boolean>(
    dist: T extends true ? IPrimeSpendDistribution : ISpendDistribution,
    lineId: number | null = null,
    isPrime: T,
  ): Observable<IDistributionResponse> {
    const params: SpendDistributionRequest = {
      budget: dist.budget,
      start_date: isPrime
        ? (dist as IPrimeSpendDistribution).prime_start_date
        : (dist as ISpendDistribution).start_date,
      duration: isPrime
        ? (dist as IPrimeSpendDistribution).prime_duration
        : (dist as ISpendDistribution).duration,
      distribution: isPrime
        ? (dist as IPrimeSpendDistribution).prime_distribution
        : (dist as ISpendDistribution).distribution,
      field: dist.field,
    };

    if (lineId) {
      params.item_id = lineId;
    }
    return this.rest.getWithObservable(`${REST_CALC_DISTRIBUTION}`, {}, params);
  }
}
